/**
 * Reads a spreadsheet in the browser, before anything is uploaded.
 *
 * This is what lets the panel refuse a file carrying CPFs without that file
 * ever leaving the school's computer. The server parses the bytes again and its
 * parse is the one that counts — but the best outcome for a minor's national ID
 * is that we never received it, and that only happens if the check runs here.
 *
 * Mirrors `apps/api/src/modules/intake/planilha.ts`: same magic-byte detection,
 * same size ceiling, same Windows-1252 fallback. Both then hand the grid to the
 * same pure validator in `@egba-do/shared`, so the preview and the server's
 * decision cannot disagree.
 */

import ExcelJS from 'exceljs'
import Papa from 'papaparse'

export const TAMANHO_MAXIMO_BYTES = 5 * 1024 * 1024

export interface Grade {
  cabecalhos: string[]
  linhas: string[][]
  formato: 'csv' | 'xlsx'
}

export class ArquivoIlegivelError extends Error {
  constructor(mensagem: string) {
    super(mensagem)
    this.name = 'ArquivoIlegivelError'
  }
}

function ehXlsx(bytes: Uint8Array, nome: string): boolean {
  // XLSX is a zip: "PK\x03\x04". Trusting the extension alone means a renamed
  // file gets the wrong reader and fails with a message nobody can act on.
  if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) return true
  return /\.xlsx?$/i.test(nome)
}

/**
 * Decodes CSV bytes.
 *
 * Strict UTF-8 first; Windows-1252 when that fails. Excel on Windows still
 * writes 1252 unless the user goes out of their way, and decoding that as UTF-8
 * turns "José da Conceição" into mojibake — a person's name, headed for the
 * official gazette.
 */
function decodificar(bytes: Uint8Array): string {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3))
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('windows-1252').decode(bytes)
  }
}

function celulaParaTexto(valor: unknown): string {
  if (valor === null || valor === undefined) return ''
  if (valor instanceof Date) return valor.toISOString().slice(0, 10)
  if (typeof valor === 'object') {
    const v = valor as { text?: unknown; result?: unknown; richText?: Array<{ text?: string }> }
    if (Array.isArray(v.richText)) return v.richText.map((x) => x.text ?? '').join('')
    if (v.text !== undefined) return String(v.text)
    if (v.result !== undefined) return String(v.result)
    return ''
  }
  return String(valor)
}

export async function lerArquivoComoGrade(arquivo: File): Promise<Grade> {
  if (arquivo.size === 0) throw new ArquivoIlegivelError('O arquivo está vazio.')
  if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
    throw new ArquivoIlegivelError(
      `O arquivo tem ${(arquivo.size / 1024 / 1024).toFixed(1)} MB, acima do limite de 5 MB. ` +
        'Uma relação de concluintes cabe em poucos KB.',
    )
  }

  const bytes = new Uint8Array(await arquivo.arrayBuffer())

  if (ehXlsx(bytes, arquivo.name)) {
    const livro = new ExcelJS.Workbook()
    try {
      await livro.xlsx.load(bytes.buffer as ArrayBuffer)
    } catch {
      throw new ArquivoIlegivelError(
        'Não consegui abrir a planilha. Salve como .xlsx ou .csv e tente de novo.',
      )
    }

    const aba = livro.worksheets[0]
    if (!aba) throw new ArquivoIlegivelError('A planilha não tem nenhuma aba.')

    const grade: string[][] = []
    aba.eachRow({ includeEmpty: false }, (linha) => {
      const valores = Array.isArray(linha.values) ? linha.values.slice(1) : []
      grade.push(valores.map(celulaParaTexto))
    })

    if (grade.length === 0) throw new ArquivoIlegivelError('A planilha está vazia.')
    const [cabecalhos, ...linhas] = grade
    return { cabecalhos: (cabecalhos ?? []).map((c) => c.trim()), linhas, formato: 'xlsx' }
  }

  const r = Papa.parse<string[]>(decodificar(bytes), {
    // Exportação brasileira usa ';' tanto quanto ','. Papa detecta pelo próprio
    // arquivo; chutar errado junta todas as colunas numa só.
    delimiter: '',
    skipEmptyLines: 'greedy',
    header: false,
  })

  const grade = (r.data ?? []).filter((l) => Array.isArray(l))
  if (grade.length === 0) throw new ArquivoIlegivelError('O arquivo CSV está vazio.')

  const [cabecalhos, ...linhas] = grade
  return {
    cabecalhos: (cabecalhos ?? []).map((c) => String(c ?? '').trim()),
    linhas: linhas.map((l) => l.map((c) => String(c ?? ''))),
    formato: 'csv',
  }
}
